import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { api } from "../lib/api";
import { cn, formatMw, formatPrice, signClass } from "../lib/utils";
import { formatTimestamp } from "../lib/format";
import { productIntervalLabel, todayBucharestIso } from "../lib/intervals";
import { useDisplay } from "../stores/display";
import { KeyValueList } from "../components/ui/key-value-list";
import type {
  BalancingRow,
  BalancingSummary,
  FxSummary,
  IdmBreakdown,
  IdmBucket,
  Market,
  MarketStats,
  PanelScope,
  Side,
  Trade,
  TradesPortfolio as Portfolio,
  TradesResponse,
} from "../features/trading/trades/contracts";
import {
  buildTradesRequestPath,
  tradesQueryKeys,
} from "../features/trading/trades/queries";
import {
  deriveIdmSummary,
  fxSummaryText,
  summarizeTradeRows,
  type IdmSummary,
} from "../features/trading/trades/selectors";
export const Route = createFileRoute("/trades")({
  component: TradesPage,
});

function TradesPage() {
  const portfoliosQ = useQuery({
    queryKey: tradesQueryKeys.portfolios(),
    queryFn: () => api<{ portfolios: Portfolio[] }>("/portfolios"),
  });
  const zone = useDisplay((s) => s.zone);
  const setZone = useDisplay((s) => s.setZone);

  const [dateFrom, setDateFrom] = useState(() => todayBucharestIso());
  const [dateTo, setDateTo] = useState(() => todayBucharestIso());
  const [market, setMarket] = useState<"" | Market>("");
  const [portfolioId, setPortfolioId] = useState("");
  const [side, setSide] = useState<"" | Side>("");
  const [productCode, setProductCode] = useState("");
  const [limit, setLimit] = useState(200);
  const [offset, setOffset] = useState(0);
  const [panelScope, setPanelScope] = useState<PanelScope>("id");

  const request = {
    deliveryDateFrom: dateFrom,
    deliveryDateTo: dateTo,
    market,
    portfolioId,
    side,
    productCode,
    limit,
    offset,
  };

  const tradesQ = useQuery({
    queryKey: tradesQueryKeys.list(request),
    queryFn: () => api<TradesResponse>(buildTradesRequestPath(request)),
  });

  return (
    <div className="min-w-0 space-y-4 p-3 sm:p-4">
      <div className="grid min-w-0 grid-cols-1 gap-3 min-[480px]:grid-cols-2 sm:flex sm:flex-wrap sm:items-end">
        <Field label="From">
          <DateInput value={dateFrom} onChange={(v) => { setDateFrom(v); setOffset(0); }} />
        </Field>
        <Field label="To">
          <DateInput value={dateTo} onChange={(v) => { setDateTo(v); setOffset(0); }} />
        </Field>
        <Field label="Market">
          <select
            value={market}
            onChange={(e) => { setMarket(e.target.value as "" | Market); setOffset(0); }}
            className={filterControlClass()}
          >
            <option value="">any</option>
            <option value="BRMID">BRMID</option>
            <option value="BRMDA">BRMDA</option>
            <option value="IMBALANCE">IMBALANCE</option>
          </select>
        </Field>
        <Field label="Portfolio">
          <select
            value={portfolioId}
            onChange={(e) => { setPortfolioId(e.target.value); setOffset(0); }}
            className={filterControlClass("sm:min-w-[16rem]")}
          >
            <option value="">any</option>
            {(portfoliosQ.data?.portfolios ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {`${p.ownerDisplayName ?? p.ownerEmail ?? "?"} · ${p.market} · ${p.brokerCode}`}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Side">
          <select
            value={side}
            onChange={(e) => { setSide(e.target.value as "" | Side); setOffset(0); }}
            className={filterControlClass()}
          >
            <option value="">any</option>
            <option value="BUY">BUY</option>
            <option value="SELL">SELL</option>
          </select>
        </Field>
        <Field label="Product code">
          <input
            type="text"
            placeholder="QH-20260424-45"
            value={productCode}
            onChange={(e) => { setProductCode(e.target.value); setOffset(0); }}
            className={filterControlClass("font-mono")}
          />
        </Field>
        <Field label="Zone">
          <select
            value={zone}
            onChange={(e) => setZone(e.target.value as "CET" | "EET")}
            className={filterControlClass()}
          >
            <option value="CET">CET</option>
            <option value="EET">EET</option>
          </select>
        </Field>
      </div>

      {tradesQ.data && (
        <>
          <SummaryPanel totals={tradesQ.data.totals} />
          <ScopedNetPanel
            scope={panelScope}
            onScopeChange={setPanelScope}
            totals={tradesQ.data.totals}
          />
          <BalancingPanel
            balancing={tradesQ.data.totals.balancing}
            zone={zone}
          />
        </>
      )}

      {tradesQ.isLoading && <div className="text-muted text-sm">Loading…</div>}
      {tradesQ.isError && (
        <div className="text-negRed text-sm">{(tradesQ.error as Error).message}</div>
      )}
      {tradesQ.data && <GroupedTables trades={tradesQ.data.trades} zone={zone} />}

      {tradesQ.data && tradesQ.data.totals.totalRows > limit && (
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted sm:flex-nowrap sm:gap-3">
          <button
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - limit))}
            className="inline-flex min-h-11 items-center justify-center rounded border border-border px-3 text-sm disabled:opacity-40 sm:min-h-0 sm:px-2 sm:py-1 sm:text-xs"
          >
            prev
          </button>
          <span>
            {offset + 1}–{Math.min(offset + limit, tradesQ.data.totals.totalRows)} of{" "}
            {tradesQ.data.totals.totalRows}
          </span>
          <button
            disabled={offset + limit >= tradesQ.data.totals.totalRows}
            onClick={() => setOffset(offset + limit)}
            className="inline-flex min-h-11 items-center justify-center rounded border border-border px-3 text-sm disabled:opacity-40 sm:min-h-0 sm:px-2 sm:py-1 sm:text-xs"
          >
            next
          </button>
          <select
            value={limit}
            onChange={(e) => { setLimit(Number(e.target.value)); setOffset(0); }}
            aria-label="Rows per page"
            className="min-h-11 rounded border border-border bg-transparent px-2 text-base sm:min-h-0 sm:px-1 sm:py-0.5 sm:text-xs"
          >
            <option value={50}>50/page</option>
            <option value={200}>200/page</option>
            <option value={500}>500/page</option>
            <option value={1000}>1000/page</option>
          </select>
        </div>
      )}
    </div>
  );
}

function DateInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <input
      type="date"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={filterControlClass()}
    />
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex min-w-0 flex-col gap-1 text-xs text-muted">
      {label}
      {children}
    </label>
  );
}

function filterControlClass(extra?: string): string {
  return cn(
    "h-11 w-full min-w-0 rounded border border-border bg-transparent px-3 text-base",
    "sm:h-auto sm:w-auto sm:px-2 sm:py-1 sm:text-sm",
    extra
  );
}

function SummaryPanel({ totals }: { totals: TradesResponse["totals"] }) {
  const daEur = totals.daEur;
  const id = totals.byCurrency.EUR;
  const combined = totals.combined;
  const fxFooter = <FxSummaryNote summary={totals.fxSummary} />;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
      {daEur ? (
        <StatsCard
          title="Day-Ahead (BRMDA)"
          accent="accent"
          stats={daEur}
          currency="EUR"
          footer={fxFooter}
        />
      ) : (
        <div className="border border-border/60 rounded p-3 text-xs text-muted">
          Day-Ahead view needs historical EUR/RON rates for the selected delivery date(s).
        </div>
      )}
      <StatsCard
        title="Intraday (BRMID)"
        accent="posGreen"
        stats={id}
        currency="EUR"
      />
      {combined ? (
        <StatsCard
          title="Combined (DA + ID)"
          accent="fg"
          stats={combined}
          currency="EUR"
          footer={fxFooter}
        />
      ) : (
        <div className="border border-border/60 rounded p-3 text-xs text-muted">
          Combined view needs historical EUR/RON rates for the selected delivery date(s).
        </div>
      )}
    </div>
  );
}

function FxSummaryNote({ summary }: { summary: FxSummary }) {
  if (summary.mode === "none") return null;
  if (summary.mode === "missing") {
    return (
      <span className="text-[10px] text-negRed">
        Missing EUR/RON for {summary.missingDates.slice(0, 3).join(", ")}
        {summary.missingDates.length > 3 ? ` +${summary.missingDates.length - 3}` : ""}
      </span>
    );
  }
  if (summary.mode === "single") {
    const rate = summary.rates[0];
    if (!rate) return null;
    return (
      <span className="text-[10px] text-muted">
        FX EUR/RON {rate.rate.toFixed(4)} · fixing {rate.asOfDate}
        {rate.asOfDate !== rate.deliveryDate ? ` for ${rate.deliveryDate}` : ""}
      </span>
    );
  }
  return (
    <span className="text-[10px] text-muted">
      Daily EUR/RON by delivery date · {summary.rates.length} dates
    </span>
  );
}

function currencySymbol(c: "EUR" | "RON"): string {
  return c === "EUR" ? "€" : "RON";
}

function StatsCard({
  title,
  accent,
  stats,
  currency,
  footer,
}: {
  title: string;
  accent: "accent" | "posGreen" | "fg";
  stats: MarketStats;
  currency: "EUR" | "RON";
  footer?: React.ReactNode;
}) {
  const sym = currencySymbol(currency);
  const accentCls =
    accent === "accent"
      ? "text-accent border-accent/40 bg-accent/5"
      : accent === "posGreen"
      ? "text-posGreen border-posGreen/40 bg-posGreen/5"
      : "text-fg border-border/60 bg-border/10";

  if (stats.rowCount === 0) {
    return (
      <div className={cn("border rounded", accentCls)}>
        <div className="px-3 py-2 font-semibold text-xs uppercase tracking-wide border-b border-current/20">
          {title}
        </div>
        <div className="p-3 text-muted text-xs italic">No trades.</div>
      </div>
    );
  }

  return (
    <div className={cn("border rounded flex flex-col", accentCls)}>
      <div className="flex flex-wrap items-center justify-between gap-1 border-b border-current/20 px-3 py-2 text-xs font-semibold uppercase tracking-wide sm:flex-nowrap sm:gap-0">
        <span>{title}</span>
        <span className="text-[10px] font-normal text-muted">
          {stats.rowCount} rows · {sym}
        </span>
      </div>
      <div className="p-3 space-y-2 text-xs font-mono">
        <Row
          label="bought"
          qty={stats.boughtMwh}
          price={stats.buyVwap}
          sym={sym}
          tone="pos"
        />
        <Row
          label="sold"
          qty={stats.soldMwh}
          price={stats.sellVwap}
          sym={sym}
          tone="neg"
        />
        <Row
          label="net"
          qty={stats.netMwh}
          price={stats.netVwap}
          sym={sym}
          tone={stats.netMwh > 0 ? "pos" : stats.netMwh < 0 ? "neg" : undefined}
          priceFallback={stats.netMwh === 0 ? "flat" : "—"}
        />
        {stats.pnl !== null && (
          <div className="mt-2 flex flex-col gap-1 border-t border-current/20 pt-2 text-sm min-[480px]:flex-row min-[480px]:items-center min-[480px]:justify-between">
            <span
              className="text-muted uppercase tracking-wider text-[10px]"
              title="P&L sums only QHs where DA + ID net to zero; open QHs are ignored"
            >
              Realized P&L
              <span className="ml-1 normal-case tracking-normal">
                ({stats.flatQhCount} QHs flat
                {stats.openQhCount > 0 ? `, ${stats.openQhCount} open` : ""})
              </span>
            </span>
            <span
              className={cn(
                "font-semibold",
                stats.pnl > 0 ? "text-posGreen" : stats.pnl < 0 ? "text-negRed" : ""
              )}
            >
              {formatPrice(stats.pnl)} {sym}
            </span>
          </div>
        )}
        {footer && <div className="pt-1">{footer}</div>}
      </div>
    </div>
  );
}

function Row({
  label,
  qty,
  price,
  sym,
  tone,
  priceFallback = "—",
}: {
  label: string;
  qty: number;
  price: number | null;
  sym: string;
  tone?: "pos" | "neg";
  priceFallback?: string;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-1 min-[480px]:flex-row min-[480px]:items-center min-[480px]:justify-between">
      <span className="text-muted">{label}</span>
      <span className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-1 font-mono tabular-nums">
        <span
          className={cn(
            tone === "pos" && "text-posGreen",
            tone === "neg" && "text-negRed"
          )}
        >
          {formatMw(qty)} MWh
        </span>
        <span className="text-muted text-[11px]">
          @{" "}
          {price !== null
            ? `${formatPrice(price)} ${sym}/MWh`
            : priceFallback}
        </span>
      </span>
    </div>
  );
}

function BalancingPanel({
  balancing,
  zone,
}: {
  balancing: BalancingSummary;
  zone: "CET" | "EET";
}) {
  const hasRows = balancing.rows.length > 0;
  const sourceSummary = hasRows
    ? `${balancing.pricedRows} priced${
        balancing.missingRows > 0 ? ` · ${balancing.missingRows} missing` : ""
      }`
    : "flat";
  return (
    <section className="min-w-0 space-y-3 rounded border border-border/60 p-3 sm:p-4">
      <div className="flex flex-wrap items-center gap-3 text-xs">
        <span className="font-semibold uppercase tracking-wide text-warnOrange">
          Balancing settlement
        </span>
        <span className="text-muted">
          · {balancing.rows.length} intervals · {sourceSummary}
        </span>
        {balancing.finalRows > 0 && (
          <span className="text-posGreen sm:ml-auto">
            {balancing.finalRows} final
          </span>
        )}
        {balancing.estimatedRows > 0 && (
          <span className={balancing.finalRows > 0 ? "text-warnOrange" : "text-warnOrange sm:ml-auto"}>
            {balancing.estimatedRows} estimated
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-x-5 gap-y-2 border-y border-border/40 py-3">
        <BalancingMetric
          label="Exposure"
          value={`${formatMw(balancing.absMwh)} MWh`}
          sub={`net ${formatMw(balancing.netMwh)} MWh`}
          className={signClass(balancing.netMwh)}
        />
        <BalancingMetric
          label="BM €/MWh"
          value={formatPrice(balancing.bmPriceEur)}
        />
        <BalancingMetric
          label="DA+ID €/MWh"
          value={formatPrice(balancing.positionPriceEur)}
        />
        <BalancingMetric
          label="Spread €/MWh"
          value={formatPrice(balancing.spreadEur)}
          className={signClass(balancing.pnlEur)}
        />
        <BalancingMetric
          label="P&L"
          value={`${formatPrice(balancing.pnlEur)} €`}
          sub={
            balancing.pnlPerMwh !== null
              ? `${formatPrice(balancing.pnlPerMwh)} €/MWh`
              : undefined
          }
          className={signClass(balancing.pnlEur)}
        />
        <BalancingMetric
          label="Cashflow"
          value={`${formatPrice(balancing.cashflowEur)} €`}
          className={signClass(balancing.cashflowEur)}
        />
      </div>

      {!hasRows ? (
        <div className="text-xs text-muted">No BM exposure for the current filters.</div>
      ) : (
        <>
          <div className="space-y-2 sm:hidden" aria-label="Balancing interval details">
            {balancing.rows.map((row) => (
              <CompactBalancingRow key={`${row.deliveryDate}-${row.qhIndex}`} row={row} zone={zone} />
            ))}
          </div>
          <div className="hidden max-h-[24rem] overflow-auto rounded-md border border-border/50 sm:block">
            <table className="min-w-full font-mono text-[11px]">
            <thead className="bg-bg sticky top-0">
              <tr className="text-muted">
                <Th>Delivery</Th>
                <Th>Interval ({zone})</Th>
                <Th className="text-right">BM MWh</Th>
                <Th>Source</Th>
                <Th className="text-right">BM €/MWh</Th>
                <Th className="text-right">DA+ID €/MWh</Th>
                <Th className="text-right">Spread €/MWh</Th>
                <Th className="text-right">P&L €</Th>
                <Th className="text-right">Cashflow €</Th>
                <Th>Data</Th>
              </tr>
            </thead>
            <tbody>
              {balancing.rows.map((r) => (
                <tr
                  key={`${r.deliveryDate}-${r.qhIndex}`}
                  className="border-t border-border/30 hover:bg-border/10"
                >
                  <Td>{r.deliveryDate}</Td>
                  <Td>{productIntervalLabel("QH", r.qhIndex, zone)}</Td>
                  <Td className={cn("text-right", signClass(r.bmNetMwh))}>
                    {formatMw(r.bmNetMwh)}
                  </Td>
                  <Td>
                    <BmSourceBadge source={r.bmPriceSource} status={r.bmDataStatus} />
                  </Td>
                  <Td className="text-right font-medium">
                    {formatPrice(r.bmAppliedPriceEur)}
                  </Td>
                  <Td className="text-right text-muted">
                    {formatPrice(r.bmPositionPriceEur)}
                  </Td>
                  <Td className={cn("text-right", signClass(r.bmPnlEur))}>
                    {formatPrice(r.bmSpreadEur)}
                  </Td>
                  <Td className={cn("text-right font-semibold", signClass(r.bmPnlEur))}>
                    {formatPrice(r.bmPnlEur)}
                  </Td>
                  <Td className={cn("text-right", signClass(r.bmSettlementEur))}>
                    {formatPrice(r.bmSettlementEur)}
                  </Td>
                  <Td className={bmDataStatusClass(r.bmDataStatus)}>
                    {r.bmDataStatus}
                  </Td>
                </tr>
              ))}
            </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}

function CompactBalancingRow({
  row,
  zone,
}: {
  row: BalancingRow;
  zone: "CET" | "EET";
}) {
  return (
    <details className="group min-w-0 rounded-lg border border-border/60 bg-bg open:border-warnOrange/50">
      <summary className="flex min-h-14 cursor-pointer list-none items-center gap-3 rounded-lg px-3 py-2 outline-none marker:hidden focus-visible:ring-2 focus-visible:ring-accent [&::-webkit-details-marker]:hidden">
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
            <span className="font-semibold text-fg">
              {productIntervalLabel("QH", row.qhIndex, zone)}
            </span>
            <span className="text-xs text-muted">{row.deliveryDate}</span>
            <BmSourceBadge source={row.bmPriceSource} status={row.bmDataStatus} />
          </div>
          <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 font-mono text-sm tabular-nums">
            <span className={signClass(row.bmNetMwh)}>{formatMw(row.bmNetMwh)} MWh</span>
            <span className={cn("font-semibold", signClass(row.bmPnlEur))}>
              P&amp;L {formatPrice(row.bmPnlEur)} €
            </span>
            <span className={bmDataStatusClass(row.bmDataStatus)}>{row.bmDataStatus}</span>
          </div>
        </div>
        <span className="shrink-0 text-lg text-muted transition-transform group-open:rotate-180" aria-hidden="true">
          ▾
        </span>
      </summary>
      <div className="border-t border-border/50 px-3 py-3">
        <KeyValueList
          ariaLabel={`${row.deliveryDate} QH-${row.qhIndex} balancing details`}
          items={[
            {
              key: "position",
              label: "Balancing position",
              value: formatMw(row.bmNetMwh),
              unit: "MWh",
              context: `${formatMw(row.bmNetMw)} MW`,
              monospace: true,
            },
            {
              key: "price",
              label: "Balancing price",
              value: formatPrice(row.bmAppliedPriceEur),
              unit: "€/MWh",
              context: `+ ${formatPrice(row.bmPositivePriceEur)} · − ${formatPrice(row.bmNegativePriceEur)}`,
              monospace: true,
            },
            {
              key: "reference",
              label: "DA + IDM reference",
              value: formatPrice(row.bmPositionPriceEur),
              unit: "€/MWh",
              monospace: true,
            },
            {
              key: "spread",
              label: "Spread",
              value: formatPrice(row.bmSpreadEur),
              unit: "€/MWh",
              monospace: true,
            },
            {
              key: "pnl",
              label: "P&L",
              value: formatPrice(row.bmPnlEur),
              unit: "€",
              monospace: true,
            },
            {
              key: "cashflow",
              label: "Cashflow",
              value: formatPrice(row.bmSettlementEur),
              unit: "€",
              monospace: true,
            },
            {
              key: "source",
              label: "Source and data",
              value: row.bmPriceSource,
              context: row.bmDataStatus,
            },
          ]}
        />
      </div>
    </details>
  );
}

function BalancingMetric({
  label,
  value,
  sub,
  className,
}: {
  label: string;
  value: string;
  sub?: string;
  className?: string;
}) {
  return (
    <div className="min-w-0">
      <div className="text-muted text-[10px] uppercase tracking-wider">{label}</div>
      <div className={cn("break-words font-mono text-sm sm:truncate", className)}>{value}</div>
      {sub && <div className="break-words font-mono text-[10px] text-muted sm:truncate">{sub}</div>}
    </div>
  );
}

function BmSourceBadge({
  source,
  status,
}: {
  source: BalancingRow["bmPriceSource"];
  status: BalancingRow["bmDataStatus"];
}) {
  const label = source === "final" ? "final" : source === "estimated" ? "est" : "—";
  const cls =
    source === "final"
      ? "border-posGreen/50 bg-posGreen/10 text-posGreen"
      : source === "estimated"
      ? "border-warnOrange/50 bg-warnOrange/10 text-warnOrange"
      : "border-border text-muted";
  return (
    <span
      className={cn("inline-flex min-w-10 justify-center rounded border px-1.5 py-0.5 text-[10px]", cls)}
      title={status}
    >
      {label}
    </span>
  );
}

function bmDataStatusClass(status: BalancingRow["bmDataStatus"]): string {
  return status === "missing"
    ? "text-negRed"
    : status === "partial"
    ? "text-warnOrange"
    : status === "final"
    ? "text-posGreen"
    : "text-muted";
}

function GroupedTables({ trades, zone }: { trades: Trade[]; zone: "CET" | "EET" }) {
  const da = trades.filter((t) => t.market === "BRMDA");
  const id = trades.filter((t) => t.market === "BRMID");
  const imb = trades.filter((t) => t.market === "IMBALANCE");

  if (trades.length === 0) {
    return <div className="text-muted text-sm">No trades match the current filters.</div>;
  }

  return (
    <div className="space-y-5">
      {da.length > 0 && (
        <MarketSection
          kind="DA"
          title="Day-Ahead Market (BRMDA)"
          trades={da}
          zone={zone}
        />
      )}
      {id.length > 0 && (
        <MarketSection
          kind="ID"
          title="Intraday Market (BRMID)"
          trades={id}
          zone={zone}
        />
      )}
      {imb.length > 0 && (
        <MarketSection
          kind="IMB"
          title="Imbalance"
          trades={imb}
          zone={zone}
        />
      )}
    </div>
  );
}

function MarketSection({
  kind,
  title,
  trades,
  zone,
}: {
  kind: "DA" | "ID" | "IMB";
  title: string;
  trades: Trade[];
  zone: "CET" | "EET";
}) {
  const totals = useMemo(() => summarizeTradeRows(trades), [trades]);

  const kindColor =
    kind === "DA"
      ? "bg-accent/15 text-accent border-accent/30"
      : kind === "ID"
      ? "bg-posGreen/10 text-posGreen border-posGreen/30"
      : "bg-negRed/10 text-negRed border-negRed/30";

  return (
    <section className="min-w-0 space-y-2">
      <div className={cn("flex min-w-0 flex-col items-start gap-2 rounded border px-3 py-2 text-xs sm:flex-row sm:items-center sm:gap-3", kindColor)}>
        <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
          <span className="font-semibold uppercase tracking-wide">{kind}</span>
          <span className="font-semibold text-fg/80">{title}</span>
          <span className="text-fg/60">· {trades.length} trades</span>
        </div>
        <span className="grid w-full min-w-0 grid-cols-1 gap-1 font-mono text-[11px] min-[480px]:grid-cols-3 sm:ml-auto sm:flex sm:w-auto sm:gap-4">
          <span>buy {formatMw(totals.bought)} @ {totals.buyVwap !== null ? formatPrice(totals.buyVwap) : "—"}</span>
          <span>sell {formatMw(totals.sold)} @ {totals.sellVwap !== null ? formatPrice(totals.sellVwap) : "—"}</span>
          <span className={cn(totals.net > 0 ? "text-posGreen" : totals.net < 0 ? "text-negRed" : "")}>
            net {formatMw(totals.net)}
          </span>
        </span>
      </div>
      <div className="space-y-2 sm:hidden" aria-label={`${title} trade details`}>
        {trades.map((trade) => (
          <CompactTradeRow key={trade.id} trade={trade} zone={zone} />
        ))}
      </div>
      <div className="hidden overflow-auto rounded-md border border-border/60 sm:block">
        <table className="min-w-full font-mono text-[11px]">
          <thead className="bg-bg sticky top-0">
            <tr className="text-muted">
              <Th>Delivery</Th>
              <Th>Interval ({zone})</Th>
              <Th>Product</Th>
              <Th>Side</Th>
              <Th className="text-right">Qty MWh</Th>
              <Th className="text-right">Price</Th>
              <Th className="text-right">Amount</Th>
              <Th>Ccy</Th>
              <Th className="text-right">Price RON</Th>
              <Th className="text-right">Amount RON</Th>
              <Th>FX</Th>
              <Th>Portfolio</Th>
              <Th>Trade Id</Th>
              <Th>Ingested</Th>
            </tr>
          </thead>
          <tbody>
            {trades.map((t) => {
              const qty = Number(t.quantityMwh);
              const interval = productIntervalLabel(t.kind, t.productIndex, zone);
              return (
                <tr key={t.id} className="border-t border-border/30 hover:bg-border/10">
                  <Td>{t.deliveryDate}</Td>
                  <Td className={t.kind === "PH" ? "text-accent" : ""}>{interval}</Td>
                  <Td>{t.productCode}</Td>
                  <Td className={t.side === "BUY" ? "text-posGreen" : t.side === "SELL" ? "text-negRed" : ""}>
                    {t.side}
                  </Td>
                  <Td className={cn("text-right", qty > 0 ? "text-posGreen" : qty < 0 ? "text-negRed" : "")}>
                    {formatMw(qty)}
                  </Td>
                  <Td className="text-right">{formatPrice(Number(t.unitPrice))}</Td>
                  <Td className="text-right">{formatPrice(Number(t.amount))}</Td>
                  <Td>{t.currency}</Td>
                  <Td className="text-right">
                    {t.unitPriceRon !== null ? formatPrice(Number(t.unitPriceRon)) : "—"}
                  </Td>
                  <Td className="text-right">
                    {t.amountRon !== null ? formatPrice(Number(t.amountRon)) : "—"}
                  </Td>
                  <Td className="text-muted">
                    {t.fxEurRon !== null ? (
                      <span title={t.fxAsOf ? `${t.fxConversionSource ?? "FX"} fixing ${t.fxAsOf}` : undefined}>
                        {t.fxEurRon.toFixed(4)}
                      </span>
                    ) : (
                      "—"
                    )}
                  </Td>
                  <Td>{t.portfolioBrokerCode ?? t.portfolioId.slice(0, 8)}</Td>
                  <Td className="text-muted">
                    {t.tradeId}
                    {t.source === "schedule" && (
                      <span
                        className="ml-1.5 inline-block rounded border border-accent/40 bg-accent/10 text-accent px-1 text-[9px] uppercase tracking-wider align-middle"
                        title="Synthesized from a DAM schedule row (confirmed DA trade)"
                      >
                        sched
                      </span>
                    )}
                  </Td>
                  <Td className="text-muted">{formatTimestamp(t.ingestedAt)}</Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function CompactTradeRow({
  trade,
  zone,
}: {
  trade: Trade;
  zone: "CET" | "EET";
}) {
  const quantity = Number(trade.quantityMwh);
  const amount = Number(trade.amount);
  const interval = productIntervalLabel(trade.kind, trade.productIndex, zone);
  const sideClass = trade.side === "BUY" ? "text-posGreen" : "text-negRed";

  return (
    <details className="group min-w-0 rounded-lg border border-border/60 bg-bg open:border-accent/40">
      <summary className="flex min-h-14 cursor-pointer list-none items-center gap-3 rounded-lg px-3 py-2 outline-none marker:hidden focus-visible:ring-2 focus-visible:ring-accent [&::-webkit-details-marker]:hidden">
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
            <span className={cn("font-semibold", sideClass)}>{trade.side}</span>
            <span className="font-medium text-fg">{interval}</span>
            <span className="text-xs text-muted">{trade.deliveryDate}</span>
            {trade.source === "schedule" && (
              <span className="rounded border border-accent/40 bg-accent/10 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-accent">
                schedule
              </span>
            )}
          </div>
          <div className="mt-1 flex min-w-0 flex-wrap gap-x-3 gap-y-1 font-mono text-sm tabular-nums">
            <span className={signClass(quantity)}>{formatMw(quantity)} MWh</span>
            <span>@ {formatPrice(Number(trade.unitPrice))} {trade.currency}/MWh</span>
            <span className="text-muted">{trade.portfolioBrokerCode ?? trade.portfolioId.slice(0, 8)}</span>
          </div>
        </div>
        <span className="shrink-0 text-lg text-muted transition-transform group-open:rotate-180" aria-hidden="true">
          ▾
        </span>
      </summary>
      <div className="border-t border-border/50 px-3 py-3">
        <KeyValueList
          ariaLabel={`${trade.tradeId} trade details`}
          items={[
            {
              key: "product",
              label: "Product",
              value: trade.productCode,
              context: `${trade.market} · ${trade.kind}`,
              monospace: true,
            },
            {
              key: "side",
              label: "Side",
              value: trade.side,
            },
            {
              key: "quantity",
              label: "Quantity",
              value: formatMw(quantity),
              unit: "MWh",
              monospace: true,
            },
            {
              key: "price",
              label: "Unit price",
              value: formatPrice(Number(trade.unitPrice)),
              unit: `${trade.currency}/MWh`,
              monospace: true,
            },
            {
              key: "amount",
              label: "Amount",
              value: formatPrice(amount),
              unit: trade.currency,
              monospace: true,
            },
            {
              key: "price-ron",
              label: "Price in RON",
              value: trade.unitPriceRon === null ? null : formatPrice(Number(trade.unitPriceRon)),
              unit: "RON/MWh",
              monospace: true,
            },
            {
              key: "amount-ron",
              label: "Amount in RON",
              value: trade.amountRon === null ? null : formatPrice(Number(trade.amountRon)),
              unit: "RON",
              monospace: true,
            },
            {
              key: "fx",
              label: "EUR/RON FX",
              value: trade.fxEurRon === null ? null : trade.fxEurRon.toFixed(4),
              context: [trade.fxConversionSource, trade.fxAsOf ? `fixing ${trade.fxAsOf}` : null]
                .filter(Boolean)
                .join(" · ") || null,
              monospace: true,
            },
            {
              key: "portfolio",
              label: "Portfolio",
              value: trade.portfolioBrokerCode ?? trade.portfolioId,
              context: trade.portfolioShortName,
            },
            {
              key: "trade-id",
              label: "Trade ID",
              value: trade.tradeId,
              context: trade.id,
              monospace: true,
            },
            {
              key: "source",
              label: "Record source",
              value: trade.source,
            },
            {
              key: "area",
              label: "Area",
              value: trade.area,
            },
            {
              key: "ingested",
              label: "Ingested",
              value: formatTimestamp(trade.ingestedAt),
              context: trade.ingestedAt,
            },
          ]}
        />
      </div>
    </details>
  );
}

function ScopedNetPanel({
  scope,
  onScopeChange,
  totals,
}: {
  scope: PanelScope;
  onScopeChange: (s: PanelScope) => void;
  totals: TradesResponse["totals"];
}) {
  const scopeMeta: Record<
    PanelScope,
    { label: string; rows: number; breakdown: IdmBreakdown; fxNote: string | null }
  > = {
    id: {
      label: "Intraday (BRMID)",
      rows: totals.byCurrency.EUR.rowCount,
      breakdown: totals.idmBreakdown,
      fxNote: null,
    },
    da: {
      label: "Day-Ahead (BRMDA)",
      rows: totals.byCurrency.RON.rowCount,
      breakdown: totals.damBreakdown,
      fxNote: fxSummaryText(totals.fxSummary, "converted via"),
    },
    combined: {
      label: "Combined (DA + ID)",
      rows: totals.byCurrency.EUR.rowCount + totals.byCurrency.RON.rowCount,
      breakdown: totals.combinedBreakdown,
      fxNote: fxSummaryText(totals.fxSummary, "DA converted to € via"),
    },
  };
  const meta = scopeMeta[scope];
  return (
    <IdmNetPanel
      title={meta.label}
      rowCount={meta.rows}
      breakdown={meta.breakdown}
      fxNote={meta.fxNote}
      scope={scope}
      onScopeChange={onScopeChange}
    />
  );
}

function IdmNetPanel({
  title,
  rowCount,
  breakdown,
  fxNote,
  scope,
  onScopeChange,
}: {
  title: string;
  rowCount: number;
  breakdown: IdmBreakdown;
  fxNote: string | null;
  scope: PanelScope;
  onScopeChange: (s: PanelScope) => void;
}) {
  const s = useMemo(
    () => deriveIdmSummary(breakdown, rowCount),
    [breakdown, rowCount]
  );

  return (
    <div className="border border-border/60 rounded p-4 space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 text-xs">
          <label className="text-muted uppercase tracking-wider text-[10px]">
            Scope:
          </label>
          <select
            aria-label="Summary scope"
            value={scope}
            onChange={(e) => onScopeChange(e.target.value as PanelScope)}
            className="h-11 min-w-0 rounded border border-border bg-transparent px-3 text-base font-semibold sm:h-auto sm:px-2 sm:py-1 sm:text-xs"
          >
            <option value="id">Intraday (BRMID)</option>
            <option value="da">Day-Ahead (BRMDA)</option>
            <option value="combined">Combined (DA + ID)</option>
          </select>
          <span className="text-muted">
            · {s.rowCount} trades · €
          </span>
        </div>
        {fxNote && (
          <span className="text-[10px] text-muted">{fxNote}</span>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <SideCard
          title="BUYS"
          tone="pos"
          cashLabel="Cash paid"
          total={s.totalBuy}
          rows={[
            { label: "QH (quarterly)", bucket: s.qBuy },
            { label: "PH (hourly)", bucket: s.hBuy },
          ]}
        />
        <SideCard
          title="SELLS"
          tone="neg"
          cashLabel="Cash received"
          total={s.totalSell}
          rows={[
            { label: "QH (quarterly)", bucket: s.qSell },
            { label: "PH (hourly)", bucket: s.hSell },
          ]}
        />
      </div>

      <NetStrip summary={s} />
    </div>
  );
}

function SideCard({
  title,
  tone,
  rows,
  total,
  cashLabel,
}: {
  title: string;
  tone: "pos" | "neg";
  rows: { label: string; bucket: IdmBucket }[];
  total: IdmBucket;
  cashLabel: string;
}) {
  const toneCls =
    tone === "pos"
      ? "text-posGreen border-posGreen/30 bg-posGreen/5"
      : "text-negRed border-negRed/30 bg-negRed/5";
  const isEmpty = total.mwh === 0;
  return (
    <div className={cn("border rounded p-3 space-y-1.5", toneCls)}>
      <div className="font-semibold uppercase tracking-wider text-[10px]">{title}</div>
      <div className="font-mono text-xs space-y-1">
        {rows.map((r) => (
          <div key={r.label} className="flex min-w-0 flex-col gap-1 min-[480px]:flex-row min-[480px]:items-center min-[480px]:justify-between">
            <span className="text-muted">{r.label}</span>
            <span>
              {formatMw(r.bucket.mwh)} MWh
              <span className="text-muted ml-2">
                {r.bucket.vwap !== null ? `@ ${formatPrice(r.bucket.vwap)} €/MWh` : "—"}
              </span>
            </span>
          </div>
        ))}
        <div className="mt-1 flex min-w-0 flex-col gap-1 border-t border-current/20 pt-1.5 font-semibold min-[480px]:flex-row min-[480px]:items-center min-[480px]:justify-between">
          <span>Total</span>
          <span>
            {formatMw(total.mwh)} MWh
            <span className="ml-2">
              {total.vwap !== null ? `@ ${formatPrice(total.vwap)} €/MWh` : "—"}
            </span>
          </span>
        </div>
        <div className="flex min-w-0 flex-col gap-1 text-muted min-[480px]:flex-row min-[480px]:items-center min-[480px]:justify-between">
          <span>{cashLabel}</span>
          <span>{isEmpty ? "0.00" : formatPrice(total.cash)} €</span>
        </div>
      </div>
    </div>
  );
}

function NetStrip({ summary }: { summary: IdmSummary }) {
  const s = summary;
  const isFlat = Math.abs(s.netMwh) < 1e-9;
  const posDir = s.netMwh > 0 ? "net bought" : s.netMwh < 0 ? "net sold" : "flat";
  const cashDir = s.netCashFlow > 0 ? "received" : s.netCashFlow < 0 ? "paid" : "—";
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-3 border-t border-border/40 text-xs">
      <StripCell label="Net position">
        <span
          className={cn(
            "font-mono text-sm",
            s.netMwh > 0 ? "text-posGreen" : s.netMwh < 0 ? "text-negRed" : ""
          )}
        >
          {formatMw(Math.abs(s.netMwh))} MWh
        </span>
        <span className="text-[10px] text-muted ml-2">({posDir})</span>
      </StripCell>
      <StripCell label="Net cash flow">
        <span
          className={cn(
            "font-mono text-sm",
            s.netCashFlow > 0 ? "text-posGreen" : s.netCashFlow < 0 ? "text-negRed" : ""
          )}
        >
          {formatPrice(Math.abs(s.netCashFlow))} €
        </span>
        <span className="text-[10px] text-muted ml-2">({cashDir})</span>
      </StripCell>
      <StripCell label={isFlat ? "Realized P&L" : "Effective net price"}>
        {isFlat ? (
          s.realizedPnl !== null ? (
            <span
              className={cn(
                "font-mono text-sm font-semibold",
                s.realizedPnl > 0
                  ? "text-posGreen"
                  : s.realizedPnl < 0
                  ? "text-negRed"
                  : ""
              )}
            >
              {formatPrice(s.realizedPnl)} €
            </span>
          ) : (
            <span className="font-mono text-muted">—</span>
          )
        ) : s.effectivePrice !== null ? (
          <>
            <span className="font-mono text-sm">
              {formatPrice(s.effectivePrice)} €/MWh
            </span>
            <span className="text-[10px] text-muted ml-2">
              (for the net {posDir})
            </span>
          </>
        ) : (
          <span className="font-mono text-muted">—</span>
        )}
      </StripCell>
    </div>
  );
}

function StripCell({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col">
      <span className="text-muted text-[10px] uppercase tracking-wider">{label}</span>
      <span>{children}</span>
    </div>
  );
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return <th className={cn("px-2 py-1.5 font-normal whitespace-nowrap text-left", className)}>{children}</th>;
}
function Td({ children, className }: { children: React.ReactNode; className?: string }) {
  return <td className={cn("px-2 py-1 whitespace-nowrap", className)}>{children}</td>;
}
